import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import NotificationPopups from "../components/notifications/NotificationPopups";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import TrialBanner from "../components/TrialBanner";
import { NotificationsProvider } from "../context/NotificationsContext";

// Data-dense screens (charts, wide tables) take the full content width; every
// other screen keeps the readable 5xl column.
const isWide = (pathname) => pathname === "/" || pathname === "/reports" || pathname.startsWith("/reports/");

export default function MainLayout({ children, user, onLogout }) {
  // Expanded by default on desktop. On mobile this same flag drives the slide-in
  // drawer, which must start closed so it does not cover the page.
  const [collapsed, setCollapsed] = useState(() => window.matchMedia("(max-width: 767.98px)").matches);
  const toggleSidebar = () => setCollapsed((c) => !c);
  const { pathname } = useLocation();

  // Publishes the sticky topbar's height as --pl-topbar-h, so a table header
  // can stick just below it while the page scrolls.
  const scrollRoot = useRef(null);
  useEffect(() => {
    const root = scrollRoot.current;
    const header = root?.querySelector(":scope > header");
    if (!header || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(() =>
      root.style.setProperty("--pl-topbar-h", `${header.offsetHeight}px`)
    );
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  return (
    <NotificationsProvider>
      {/* Exactly the viewport tall (dvh where supported, so mobile browser chrome is
          not counted) and clipped: the content column below is the one scrollbar. */}
      <div className="flex h-screen overflow-hidden bg-raised supports-[height:100dvh]:h-[100dvh]">
        <Sidebar collapsed={collapsed} user={user} />

        {/* Mobile backdrop */}
        {!collapsed && <div className="fixed inset-0 z-30 bg-black/40 md:hidden" onClick={toggleSidebar} />}

        {/* The scrolling area — list screens save and restore its position (useScrollRestoration).
            `relative` makes it the containing block of absolutely positioned descendants
            (sr-only lists, tooltips); without it they were placed against the document and
            stretched <body> past the shell, adding page scrollbars and an empty band below. */}
        <div
          ref={scrollRoot}
          data-scroll-root
          className="relative flex min-w-0 flex-1 flex-col overflow-auto"
        >
          <Topbar user={user} onLogout={onLogout} onToggleSidebar={toggleSidebar} />
          {/* Free trial: days left, or ended (choose a plan). */}
          <TrialBanner user={user} />

          <main className="min-w-0 flex-1 px-4 py-6 md:px-6">
            <div
              className={isWide(pathname) ? "mx-auto w-full max-w-screen-2xl" : "container mx-auto max-w-5xl"}
            >
              {children}
            </div>
          </main>
        </div>
      </div>

      <NotificationPopups />
    </NotificationsProvider>
  );
}
